/**
 * Round-trip for Medical Record tab composer notes: `meta.tag` kind + `title` item when no template resolves.
 */

import type { Practitioner, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import {
  CHART_NOTE_KIND_ADMIN,
  CHART_NOTE_KIND_CHART,
  CHART_NOTE_KIND_TAG_SYSTEM,
} from '@unify/fhir-model/constants';
import type { ClinicalNote } from '../../pages/clients/data/types';

export const COMPOSED_NOTE_TITLE_LINK_ID = 'title';

export function composedChartNoteTag(kind: 'chart' | 'admin'): { system: string; code: string } {
  return {
    system: CHART_NOTE_KIND_TAG_SYSTEM,
    code: kind === 'admin' ? CHART_NOTE_KIND_ADMIN : CHART_NOTE_KIND_CHART,
  };
}

export function composedChartNoteType(qr: QuestionnaireResponse): ClinicalNote['noteType'] | undefined {
  const code = qr.meta?.tag?.find((tag) => tag.system === CHART_NOTE_KIND_TAG_SYSTEM)?.code;
  if (code === CHART_NOTE_KIND_CHART) return 'Chart Note';
  if (code === CHART_NOTE_KIND_ADMIN) return 'Administrative Note';
  return undefined;
}

/**
 * Stamp authorship and patient compartment before create.
 *
 * Provider CareTeam policy admits via `author=%profile` — missing author → Forbidden.
 * Client-supplied `meta.accounts` does not satisfy `_compartment=%care_team` at create time,
 * but stamping Patient CareTeam accounts keeps the note visible to every panel clinician.
 */
export function applyComposedNoteAuthorship(
  qr: QuestionnaireResponse,
  opts: { author?: Reference<Practitioner>; accounts?: Reference[] },
): QuestionnaireResponse {
  const { author, accounts } = opts;
  return {
    ...qr,
    ...(author ? { author, source: author } : {}),
    ...(accounts?.length ? { meta: { ...qr.meta, accounts } } : {}),
  };
}

export function composedNoteTitle(qr: QuestionnaireResponse): string | undefined {
  const title = qr.item?.find((item) => item.linkId === COMPOSED_NOTE_TITLE_LINK_ID)?.answer?.[0]
    ?.valueString;
  return title?.trim() || undefined;
}
